import { Vector2 } from "../../../common/vector2.js";
import { MoveDirection } from "../../../common/types.js";
import { randomPointInCircle } from "../../../common/visual_fx_common_funcs.js";
import { CatmulromPrecomputed } from "../../utils/catmulrom_precomputed.js";
import { AMPLITUDE_TRAIL, RATE, LIFE_SPAN_TRAIL, FIREWORKS_COLORS } from "./fireworks_constants.js";
import { FireworksColor } from "./fireworks_types.js";
import { PayloadEmitter } from "../../core/emitters/payload_emitter.js";
import { VfxFireworksBatch } from "./vfx_fireworks_batch.js";
import { ImgID } from "../../../image_loader/img_id.js";

export class VfxFireworks_Sparks
{
  private static readonly LIFE_SPAN          : number = LIFE_SPAN_TRAIL;
  private static readonly PARTICLE_LIFE_SPAN : number = 300;
  private static readonly BREAKPOINT         : number = 0.5;
  private static readonly RATE               : number = RATE;
  private static readonly AMPLITUDE          : number = AMPLITUDE_TRAIL;
  private static readonly COUNT              : number = 3;
  private static readonly CIRCLE_RADIUS      : number = 10;
  private static readonly SIZE               : number = 8;

  public static spawn( batch: VfxFireworksBatch, from: Vector2, to: Vector2, color_type: FireworksColor, amplitude_direction: MoveDirection, now: number ): void
  {
    const color = FIREWORKS_COLORS.get( color_type );
    if ( !color ) throw new Error( `No value found for key: ${color_type}` );

    const scale = LayoutScaler.getScale();
    const amplitude = VfxFireworks_Sparks.AMPLITUDE * amplitude_direction;

    const precomputed = CatmulromPrecomputed.calcPrecomputed( from, to, amplitude );
    
    // Calculate approximate spline length (straight line dist from p1 to p2) to determine particle count
    const dx = precomputed.p2.x - precomputed.p1.x;
    const dy = precomputed.p2.y - precomputed.p1.y;
    const dist = Math.hypot( dx, dy );
    
    // Matches the old rate_over_distance logic
    const steps = Math.max( 1, Math.floor( dist * ( VfxFireworks_Sparks.RATE / scale ) ) );
    const count_per_step = VfxFireworks_Sparks.COUNT;
    
    for ( let i = 0; i < steps; i++ )
    {
      const curve_t = steps > 1 ? i / (steps - 1) : 0.5;
      const step_start_time = now + curve_t * VfxFireworks_Sparks.LIFE_SPAN;
      
      for ( let c = 0; c < count_per_step; c++ )
      {
        const offset = randomPointInCircle( new Vector2( 0, 0 ), VfxFireworks_Sparks.CIRCLE_RADIUS * scale );
        batch.spawnParticle(
          ImgID.CircleGlow1,
          precomputed.p1, precomputed.bp1, precomputed.bp2, precomputed.p2,
          offset,
          curve_t, // It's stationary at curve_t
          color,
          VfxFireworks_Sparks.SIZE * scale,
          VfxFireworks_Sparks.SIZE * scale,
          1.0,
          0.0,
          VfxFireworks_Sparks.BREAKPOINT,
          0.0, // additive
          step_start_time,
          VfxFireworks_Sparks.PARTICLE_LIFE_SPAN
        );
      }
    }
  }
}
